/**
 * ProjectsNavDropdown — the Projects nav pill with a hover/click-to-open
 * dropdown listing the user's most-recently-active projects. Click the
 * pill itself to go to /projects; click a row to jump into that
 * project's detail page.
 *
 * Data source: GET /projects?withCounts=true (already used elsewhere).
 * TanStack Query caches the result so opening the dropdown is instant
 * after the first pull.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/apiClient.ts';

// Matches StudioNavLink's pill treatment exactly so the dropdown
// sits seamlessly in the nav row. Drops the per-pill border because
// StudioNavLink now lives inside a single bone-tinted pill-group
// container — individual borders would double-up.
const BASE_CLASS =
  'inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-sm font-medium transition-colors';
const IDLE_VARIANT =
  'text-charcoal hover:bg-pine/10 hover:text-pine';
const ACTIVE_VARIANT = 'bg-pine text-cloud';
const OPEN_VARIANT = 'bg-pine/10 text-pine';

interface ProjectSummary {
  id: string;
  name: string;
  meetingCount: number;
  finalizedCount: number;
  draftCount: number;
  lastActivityAt: string | null;
}

interface Props {
  active: boolean;
}

const MAX_RECENT = 6;

export function ProjectsNavDropdown({ active }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Only fetch when the dropdown's actually opened so we don't hit
  // /projects on every page load.
  const projectsQuery = useQuery({
    queryKey: ['projects-with-counts', 'nav-dropdown'],
    queryFn: () =>
      api.get<{ projects: ProjectSummary[] }>('/projects', {
        withCounts: true,
        limit: 50,
      }),
    enabled: open,
    staleTime: 60_000,
  });

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Hover-intent: open on hover with a tiny delay, close on leave with
  // a short grace period so diagonal mouse paths to the dropdown don't
  // dismiss it.
  function handleEnter() {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setOpen(true);
  }
  function handleLeave() {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpen(false), 180);
  }

  const recent = (projectsQuery.data?.projects ?? [])
    .filter((p) => p.meetingCount > 0)
    // Server already sorts recency DESC when withCounts=true, but be
    // explicit so a server-side ordering change doesn't silently
    // reshuffle the dropdown.
    .slice()
    .sort((a, b) => {
      const aT = a.lastActivityAt ? Date.parse(a.lastActivityAt) : 0;
      const bT = b.lastActivityAt ? Date.parse(b.lastActivityAt) : 0;
      return bT - aT;
    })
    .slice(0, MAX_RECENT);

  // Pill variant driven by active/open state. When the dropdown is
  // open (not navigated yet but the user's browsing recents) we use
  // the "open" variant — pine border + soft pine tint — as a visual
  // signal that it's distinct from the solid active state.
  const variant = active ? ACTIVE_VARIANT : open ? OPEN_VARIANT : IDLE_VARIANT;

  return (
    <div
      ref={rootRef}
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <Link to="/projects" className={`${BASE_CLASS} ${variant}`}>
        Projects
        <span aria-hidden className="text-[10px] opacity-70">
          ▾
        </span>
      </Link>

      {open && (
        <div
          className="absolute left-1/2 top-full z-50 mt-2 w-72 -translate-x-1/2 overflow-hidden rounded-lg border border-smoke/30 bg-white text-sm"
          style={{ boxShadow: '0 12px 32px rgba(43, 42, 47, 0.12)' }}
        >
          <div className="border-b border-smoke/20 bg-cloud/40 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-charcoal/60">
            Recent projects
          </div>

          {projectsQuery.isLoading && (
            <div className="px-4 py-4 text-xs italic text-smoke">Loading…</div>
          )}
          {!projectsQuery.isLoading && recent.length === 0 && (
            <div className="px-4 py-4 text-xs italic text-smoke">
              No projects with meetings yet.
            </div>
          )}

          {recent.length > 0 && (
            <ul className="divide-y divide-smoke/20">
              {recent.map((p) => (
                <li key={p.id}>
                  <Link
                    to={`/projects/${p.id}`}
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between gap-3 px-4 py-2 transition-colors hover:bg-cloud/50"
                  >
                    <span className="truncate text-charcoal">{p.name}</span>
                    <span className="shrink-0 text-[11px] text-smoke">
                      {p.meetingCount} {p.meetingCount === 1 ? 'mtg' : 'mtgs'}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <Link
            to="/projects"
            onClick={() => setOpen(false)}
            className="block border-t border-smoke/20 bg-cloud/20 px-4 py-2 text-center text-[11px] font-medium text-pine transition-colors hover:bg-cloud/50"
          >
            View all projects →
          </Link>
        </div>
      )}
    </div>
  );
}
